import {
  ActionBar,
  Box,
  Button,
  Popover,
  useViewport,
  ViewportProvider,
  WithPopover,
} from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import React from 'react'
import { ApiProduct } from '@vend/types'
import {
  FulfilmentDetail,
  FulfilmentType,
  isDefinedFulfilmentType,
} from '../../../../../models/fulfilment'
import { FormButtonOnEmptyShippingAddress } from '../address/FormButtonOnEmptyShippingAddress'
import { useSaleHasServiceOrder } from '../../../useSaleHasServiceOrder'
import {
  CurrentView,
  LineItemFulfillmentProvider,
  useLineItemFulfillmentContext,
} from './LineItemFulfillmentContext'
import { LineItemFulfilmentCustomerDetailsView } from './LineItemFulfilmentCustomerDetailsView'
import { LineItemFulfilmentNewCustomerDetailsView } from './LineItemFulfilmentNewCustomerDetailsView'
import { LineItemFulfilmentAddCustomerView } from './LineItemFulfilmentAddCustomerView'
import { LineItemFulfilmentDetailsView } from './LineItemFulfilmentDetailsView'
import { LineItemFulfilmentDecisionView } from './LineItemFulfilmentDecisionView'
import {
  useLineItemFulfillmentContinueButtonStatus,
  useLineItemFulfillmentCreateNewCustomerFlow,
  useLineItemFulfillmentCustomerDetailsChange,
  useLineItemFulfillmentDecisionFlow,
  useLineItemFulfillmentDetailsChange,
  useLineItemFulfillmentReviewContactInfoFlow,
  useLineItemFulfillmentSaleCustomer,
} from './hooks'
import { LineItemFulfilmentFixSaleAddresses } from './LineItemFulfilmentFixSaleAddresses'
import {
  AddressErrorsStatus,
  useFulfillmentHelper,
} from './hooks/useFulfillmentHelper'

type Props = {
  children: React.ReactNode
  open: boolean
  productQuantity: number
  productName: string
  onFulfilmentDetailsSet: (fulfilmentDetails: FulfilmentDetail) => void
  onClose: () => void
  defaultView?: CurrentView
  defaultFulfilmentDetail?: FulfilmentDetail
  onSelectContinueAnyway?: () => void
  onSelectFulfilmentType?: (type: FulfilmentType) => void
  canShowBreakdownOption?: boolean
  outerProduct?: ApiProduct
  breakdownOuterPacks?: () => Promise<boolean>
  isBreakingDown?: boolean
}

type ComponentProps = Omit<Props, 'children' | 'open'>

export const TContext = 'Line item fulfilment popover'

const WithResponsivePopover: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const { ExtraSmall, Small, Medium } = useViewport()
  return (
    <WithPopover
      {...(ExtraSmall || Small || Medium
        ? {
            direction: 'below' as const,
          }
        : {
            direction: 'left' as const,
            alignment: 'start' as const,
          })}
    >
      {children}
    </WithPopover>
  )
}

const FulfillmentPopoverWrapper: React.FC<Props> = ({
  children,
  open,
  productQuantity,
  productName,
  onFulfilmentDetailsSet,
  onClose,
  onSelectContinueAnyway,
  canShowBreakdownOption = false,
  outerProduct,
  breakdownOuterPacks,
}: Props) => {
  return (
    <ViewportProvider>
      <WithResponsivePopover>
        {children}
        {open && (
          <LineItemFulfilmentPopoverComponent
            productQuantity={productQuantity}
            productName={productName}
            onFulfilmentDetailsSet={onFulfilmentDetailsSet}
            onClose={onClose}
            onSelectContinueAnyway={onSelectContinueAnyway}
            canShowBreakdownOption={canShowBreakdownOption}
            outerProduct={outerProduct}
            breakdownOuterPacks={breakdownOuterPacks}
          />
        )}
      </WithResponsivePopover>
    </ViewportProvider>
  )
}

const LineItemFulfilmentPopoverComponent: React.FC<ComponentProps> = ({
  productQuantity,
  productName,
  onFulfilmentDetailsSet,
  onClose,
  canShowBreakdownOption,
  outerProduct,
  breakdownOuterPacks,
  isBreakingDown,
}: ComponentProps) => {
  const saleHasServiceOrder = useSaleHasServiceOrder()

  const {
    // Keep track of what the current view is
    currentView,
    // Keep track of the fulfillment decision made in the decision view
    currentDecision,
    // Current Customer keeps track of the customer object we are working with
    currentCustomer,
    // Customer group for the current customer
    customerGroup,
    // Disable the Continue button and display an error message if the email is invalid
    isEmailInvalid,
    // Fulfillment details for the Fulfillment Details view
    fulfilmentDetails,
    onSelectFulfilmentType,
  } = useLineItemFulfillmentContext()

  const {
    handleFirstNameChange,
    handleLastNameChange,
    handleEmailChange,
    handlePhoneNumberChange,
  } = useLineItemFulfillmentCustomerDetailsChange()

  const {
    handleSelectFulfillmentType,
    handleNoteChange,
  } = useLineItemFulfillmentDetailsChange()

  const {
    isContinueButtonDisabled,
  } = useLineItemFulfillmentContinueButtonStatus()

  const {
    onAddNewCustomer,
    onSelectCustomer,
    onRemoveCustomer,
  } = useLineItemFulfillmentSaleCustomer()

  const {
    handleContinueFromCreateNewCustomerView,
  } = useLineItemFulfillmentCreateNewCustomerFlow({
    onClose,
    saleHasServiceOrder,
  })
  const {
    handleContinueFromReviewContactInfoView,
  } = useLineItemFulfillmentReviewContactInfoFlow({
    onClose,
    saleHasServiceOrder,
  })

  const {
    handleContinueFromDecisionView,
    handleDecisionSelect,
  } = useLineItemFulfillmentDecisionFlow({
    onFulfilmentDetailsSet,
    onClose,
    onBreakdownOuterPacks: breakdownOuterPacks,
    saleHasServiceOrder,
  })

  const { getFulfillmentAddressStatus } = useFulfillmentHelper()
  const status = getFulfillmentAddressStatus(fulfilmentDetails)

  const isEmptyShippingAndNotEmptyBillingAddress =
    status === AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING ||
    status === AddressErrorsStatus.EMPTYSHIPPING_INCORRECTBILLING

  const showWidePopover =
    currentView === CurrentView.FIX_SALE_ADDRESSES &&
    isEmptyShippingAndNotEmptyBillingAddress

  const onClickContinue = () => {
    switch (currentView) {
      case CurrentView.DECISION:
        handleContinueFromDecisionView()
        break
      case CurrentView.REVIEW_CONTACT_INFO:
        handleContinueFromReviewContactInfoView()
        break
      case CurrentView.CREATE_NEW_CUSTOMER:
        handleContinueFromCreateNewCustomerView()
        break
      case CurrentView.FULFILMENT_METHOD:
        if (fulfilmentDetails.type === FulfilmentType.DELIVERY) {
          handleContinueFromReviewContactInfoView(fulfilmentDetails)
        } else {
          if (onSelectFulfilmentType) {
            onSelectFulfilmentType(fulfilmentDetails.type)
          }
          // At this point we are in the fulfilment method view
          // Upper component should update the line item
          onFulfilmentDetailsSet(fulfilmentDetails)
          onClose()
        }
        break
      default:
        break
    }
  }

  const renderView = () => {
    switch (currentView) {
      case CurrentView.DECISION:
        return (
          <LineItemFulfilmentDecisionView
            decision={currentDecision}
            productName={productName}
            productQuantity={productQuantity}
            onSelect={handleDecisionSelect}
            canShowBreakdownOption={canShowBreakdownOption}
            outerProduct={outerProduct}
            saleHasServiceOrder={saleHasServiceOrder}
          />
        )
      case CurrentView.ADD_CUSTOMER:
        return (
          <LineItemFulfilmentAddCustomerView
            onSelectCustomer={onSelectCustomer}
            onAddNewCustomer={onAddNewCustomer}
          />
        )
      case CurrentView.CREATE_NEW_CUSTOMER:
        return (
          <LineItemFulfilmentNewCustomerDetailsView
            customer={currentCustomer}
            onFirstNameChange={handleFirstNameChange}
            onLastNameChange={handleLastNameChange}
            onEmailChange={handleEmailChange}
            onPhoneNumberChange={handlePhoneNumberChange}
            isEmailInvalid={isEmailInvalid}
          />
        )
      case CurrentView.REVIEW_CONTACT_INFO:
        return (
          currentCustomer && (
            <LineItemFulfilmentCustomerDetailsView
              onRemoveCustomer={onRemoveCustomer}
              onEmailChange={handleEmailChange}
              onPhoneNumberChange={handlePhoneNumberChange}
              isEmailInvalid={isEmailInvalid}
              customer={currentCustomer}
              customerGroup={customerGroup}
            />
          )
        )
      case CurrentView.FIX_SALE_ADDRESSES:
        return <LineItemFulfilmentFixSaleAddresses />

      default:
        return (
          <LineItemFulfilmentDetailsView
            fulfilmentType={
              isDefinedFulfilmentType(fulfilmentDetails.type)
                ? fulfilmentDetails.type
                : FulfilmentType.DELIVERY
            }
            fulfilmentNote={fulfilmentDetails.note}
            productName={productName}
            onSelect={handleSelectFulfillmentType}
            onNoteChange={handleNoteChange}
          />
        )
    }
  }

  const renderButtons = () => {
    if (currentView !== CurrentView.FIX_SALE_ADDRESSES) {
      return (
        <Button
          variant="go"
          data-cy="continue-fulfilment-button"
          onClick={onClickContinue}
          disabled={isContinueButtonDisabled()}
          data-track="continue-fulfilment"
          loading={isBreakingDown}
        >
          <T _str="Continue" _context={TContext} />
        </Button>
      )
    }

    return (
      <Box className="vd-btn-group">
        <FormButtonOnEmptyShippingAddress
          fulfilmentDetail={fulfilmentDetails}
          onClose={onClose}
        />
      </Box>
    )
  }

  return (
    <Popover
      className={
        showWidePopover ? 'fulfillment-popover-wide' : 'fulfillment-popover'
      }
      onClose={onClose}
      actions={
        <ActionBar className="vd-pt3 vd-pb3">
          <div /> {/* Empty Placeholder */}
          {renderButtons()}
        </ActionBar>
      }
    >
      {renderView()}
    </Popover>
  )
}

export const LineItemFulfilmentPopoverMemo = React.memo(
  FulfillmentPopoverWrapper
)

export const LineItemFulfilmentPopover = (props: Props) => (
  <LineItemFulfillmentProvider
    defaultView={props.defaultView}
    defaultFulfillmentDetail={props.defaultFulfilmentDetail}
    onSelectContinueAnyway={props.onSelectContinueAnyway}
    onSelectFulfilmentType={props.onSelectFulfilmentType}
  >
    <LineItemFulfilmentPopoverMemo {...props} />
  </LineItemFulfillmentProvider>
)
